import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { FileStack, UserIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import type { OrderRecordDetailListItem } from '@/entities/record/model/types';
import { api, getErrorMessage } from '@/shared/api/api';
import { PRESIGNED_URL_VIEW_MODE } from '@/shared/types/common';
import { Checkbox } from '@/shared/ui';
import { Button } from '@/shared/ui/button';
import { Separator } from '@/shared/ui/separator';
import { Text } from '@/shared/ui/text';
import { DataTable } from './data-table';

const createOrderRecordColumns = (recordId: string): ColumnDef<OrderRecordDetailListItem>[] => [
  {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        aria-label="전체 선택"
        checked={
          table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        aria-label="행 선택"
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'created_at',
    header: '일시',
    cell: ({ row }) => (
      <Text typography="typo-text-xl">
        {format(new Date(row.getValue('created_at')), 'yyyy-MM-dd HH:mm')}
      </Text>
    ),
  },
  {
    accessorKey: 'product_name',
    header: '제품명/회사명',
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        <Text typography="typo-text-xl">{row.original.product_name}</Text>
      </div>
    ),
  },
  {
    accessorKey: 'company_name',
    header: '자사용 기록서(원본)',
    cell: ({ row }) => <Text typography="typo-text-xl">{row.original.source_file_name}</Text>,
  },
  {
    accessorKey: 'record.history_count',
    header: '기록서',
    cell: ({ row }) => (
      <Button
        onClick={async () => {
          toast.promise(
            async () => {
              const { file_url } = await api.post<{ file_url: string }>(
                `contractor-records/${recordId}/file`,
                {
                  item_ids: [row.original.id],
                  file_type: 'pdf',
                  view_mode: PRESIGNED_URL_VIEW_MODE.PREVIEW,
                }
              );

              window.open(file_url, '_blank');

              return file_url;
            },
            {
              loading: '기록서 미리보기 준비 중입니다. 잠시만 기다려 주세요.',
            }
          );
        }}
        size={'icon'}
        variant={'link'}
      >
        보기
      </Button>
      // <Link
      //   className="underline text-blue-500"
      //   href={row.original.source_file_document_url}
      //   target="_blank"
      // >
      //   보기
      // </Link>
    ),
  },
  {
    accessorKey: 'download',
    header: '다운로드',
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        <Button
          className="size-6"
          onClick={() => {
            window.open(row.original.word_document_url, '_blank');
          }}
          size={'icon'}
          variant={'ghost'}
        >
          <svg
            fill="none"
            height="24"
            viewBox="0 0 24 24"
            width="24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <title>w</title>
            <path
              d="M22.9776 1H6.6051C6.04008 1 5.58203 1.45804 5.58203 2.02307V6.58139L15.0704 9.37209L24.0006 6.58139V2.02307C24.0006 1.45804 23.5426 1 22.9776 1Z"
              fill="#41A5EE"
            />
            <path
              d="M24.0006 6.58154H5.58203V12.1629L15.0704 13.8374L24.0006 12.1629V6.58154Z"
              fill="#2B7CD3"
            />
            <path
              d="M5.58203 12.1631V17.7445L14.5123 18.8608L24.0006 17.7445V12.1631H5.58203Z"
              fill="#185ABD"
            />
            <path
              d="M6.6051 23.3255H22.9776C23.5426 23.3255 24.0006 22.8675 24.0006 22.3025V17.7441H5.58203V22.3025C5.58203 22.8675 6.04008 23.3255 6.6051 23.3255Z"
              fill="#103F91"
            />
            <path
              d="M12.3729 5.46484H5.58203V19.4183H12.3729C12.9372 19.4165 13.3941 18.9595 13.396 18.3953V6.48792C13.3941 5.92365 12.9372 5.46668 12.3729 5.46484Z"
              fill="black"
              opacity="0.1"
            />
            <path
              d="M11.8148 6.02344H5.58203V19.9769H11.8148C12.379 19.9751 12.836 19.5181 12.8378 18.9538V7.04651C12.836 6.48225 12.379 6.02527 11.8148 6.02344Z"
              fill="black"
              opacity="0.2"
            />
            <path
              d="M11.8148 6.02344H5.58203V18.8606H11.8148C12.379 18.8588 12.836 18.4018 12.8378 17.8376V7.04651C12.836 6.48225 12.379 6.02527 11.8148 6.02344Z"
              fill="black"
              opacity="0.2"
            />
            <path
              d="M11.2566 6.02344H5.58203V18.8606H11.2566C11.8209 18.8588 12.2779 18.4018 12.2797 17.8376V7.04651C12.2779 6.48225 11.8209 6.02527 11.2566 6.02344Z"
              fill="black"
              opacity="0.2"
            />
            <path
              d="M1.02307 6.02344H11.256C11.821 6.02344 12.2791 6.48148 12.2791 7.04651V17.2794C12.2791 17.8445 11.821 18.3025 11.256 18.3025H1.02307C0.458044 18.3025 0 17.8445 0 17.2794V7.04651C0 6.48148 0.458044 6.02344 1.02307 6.02344Z"
              fill="url(#paint0_linear_9770_68889)"
            />
            <path
              d="M4.19826 13.8694C4.21836 14.0273 4.23175 14.1646 4.23789 14.2824H4.26132C4.27025 14.1708 4.28885 14.0363 4.31714 13.8789C4.34542 13.7215 4.37091 13.5884 4.39361 13.4798L5.4697 8.83719H6.86114L7.97742 13.41C8.04228 13.6944 8.08871 13.9826 8.1164 14.2729H8.13482C8.15563 13.9916 8.19441 13.712 8.25092 13.4357L9.14115 8.83496H10.4076L8.84365 15.488H7.36403L6.30357 11.082C6.27287 10.9553 6.23771 10.7896 6.1992 10.5858C6.16068 10.3821 6.13668 10.2331 6.12719 10.1393H6.10877C6.0965 10.2476 6.07249 10.4084 6.03677 10.6216C6.00105 10.8348 5.97239 10.9925 5.95081 11.0949L4.95397 15.4869H3.44922L1.87695 8.83718H3.16625L4.13574 13.4893C4.15752 13.5847 4.17817 13.712 4.19826 13.8694Z"
              fill="white"
            />
            <defs>
              <linearGradient
                gradientUnits="userSpaceOnUse"
                id="paint0_linear_9770_68889"
                x1="2.13312"
                x2="10.1459"
                y1="5.22403"
                y2="19.1019"
              >
                <stop stopColor="#2368C4" />
                <stop
                  offset="0.5"
                  stopColor="#1A5DBE"
                />
                <stop
                  offset="1"
                  stopColor="#1146AC"
                />
              </linearGradient>
            </defs>
          </svg>
        </Button>
        <Button
          className="size-6"
          onClick={async () => {
            toast.promise(
              async () => {
                const { file_url } = await api.post<{ file_url: string }>(
                  `contractor-records/${recordId}/file`,
                  {
                    item_ids: [row.original.id],
                    file_type: 'pdf',
                    view_mode: PRESIGNED_URL_VIEW_MODE.DOWNLOAD,
                  }
                );

                window.open(file_url, '_blank');

                return file_url;
              },
              {
                loading: '기록서 다운로드 중입니다. 잠시만 기다려 주세요.',
              }
            );
          }}
          size={'icon'}
          variant={'ghost'}
        >
          <svg
            fill="none"
            height="24"
            viewBox="0 0 24 24"
            width="24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <title>pdf</title>
            <path
              d="M19.75 0C22.1 0 24 1.94912 24 4.35938V19.6406C24 22.0509 22.1 24 19.75 24H4.25C1.9 24 0 22.0509 0 19.6406V4.35938C0 1.94912 1.9 0 4.25 0H19.75Z"
              fill="#E01C12"
            />
            <path
              d="M14.1533 18C14.3868 18 14.5861 18.0422 14.751 18.126C14.9171 18.2084 15.0433 18.3228 15.1299 18.4697C15.2178 18.6153 15.2627 18.7819 15.2627 18.9688C15.2626 19.1622 15.2177 19.3287 15.1299 19.4688C15.0433 19.6088 14.9172 19.7164 14.751 19.792C14.5861 19.8675 14.3868 19.9062 14.1533 19.9062H13.5664V21H13V18H14.1533ZM16.6162 18C16.8166 18 16.9993 18.0333 17.1641 18.1006C17.3303 18.1679 17.4738 18.2652 17.5947 18.3916C17.7169 18.5166 17.8105 18.6664 17.875 18.8408C17.9409 19.0152 17.9736 19.2102 17.9736 19.4258V19.5762C17.9736 19.7903 17.9409 19.9854 17.875 20.1611C17.8105 20.3355 17.7169 20.4854 17.5947 20.6104C17.4739 20.7353 17.3297 20.8321 17.1621 20.8994C16.9945 20.9667 16.8082 21 16.6035 21H15.707V18H16.6162ZM20.3906 18.4473H19.0225V19.2959H20.2324V19.7432H19.0225V21H18.4561V18H20.3906V18.4473ZM16.2734 20.5547H16.6035C16.7778 20.5547 16.9239 20.5163 17.042 20.4395C17.1601 20.3612 17.2501 20.2491 17.3105 20.1035C17.3709 19.9566 17.4014 19.7807 17.4014 19.5762V19.4219C17.4014 19.2641 17.3839 19.1247 17.3496 19.0039C17.3153 18.883 17.2646 18.7807 17.1973 18.6982C17.1314 18.6159 17.0496 18.5533 16.9521 18.5107C16.8547 18.4682 16.7425 18.4473 16.6162 18.4473H16.2734V20.5547ZM10.1328 5.21094C10.2141 3.69026 12.0274 3.50508 12.501 5.21094C12.8799 6.57565 12.2707 8.76842 11.8828 10.334C12.2709 11.2546 13.5378 12.9602 14.293 13.7139C16.8145 13.3172 18.2355 13.3611 19.1699 13.8193C20.4371 14.4408 20.1682 15.9708 18.9395 16.0947C17.8898 16.2005 16.3403 16.0941 14.1387 14.2031C13.5665 14.2826 11.8625 14.6237 9.8252 15.3535C9.04445 16.6493 7.95673 18.4218 7.03516 19.1885C5.52494 20.4445 4.38565 20.0351 4.06543 19.3477C3.63022 18.1311 5.39645 16.9933 8.60938 15.3535C9.11717 14.3131 10.2884 11.7118 10.9131 9.62793C10.64 8.9535 10.0304 7.12839 10.1328 5.21094ZM8.17383 16.0674C3.7961 18.019 4.00108 19.3346 4.82031 19.4668C5.63954 19.599 6.65058 18.527 8.17383 16.0674ZM13.5664 19.4609H14.1533C14.2797 19.4609 14.3826 19.4406 14.4609 19.3994C14.5406 19.3568 14.5987 19.2984 14.6357 19.2256C14.6727 19.1515 14.6914 19.0673 14.6914 18.9727C14.6914 18.8806 14.6728 18.7945 14.6357 18.7148C14.5987 18.6353 14.5404 18.5709 14.4609 18.5215C14.3826 18.472 14.2797 18.4473 14.1533 18.4473H13.5664V19.4609ZM18.8877 14.9043C18.8365 13.6612 16.9934 13.6608 14.5869 14.084C17.3901 16.1468 18.8875 15.777 18.8877 14.9043ZM11.6689 10.8311C10.9931 12.894 10.337 14.3443 10.0938 14.8115C11.9164 14.2297 13.3624 13.9168 13.8574 13.833C12.8437 12.6482 11.9762 11.338 11.6689 10.8311ZM11.041 4.4707C10.1967 4.61696 10.3117 7.10255 11.1436 8.9668C12.206 5.46248 11.8474 4.3517 11.041 4.4707Z"
              fill="white"
            />
          </svg>
        </Button>
      </div>
    ),
  },
];

export function RecordDetailClient({ recordId }: { recordId: string }) {
  const [selectedRows, setSelectedRows] = useState<OrderRecordDetailListItem[]>([]);
  const navigate = useNavigate();

  const { data: tableData, isLoading } = useQuery({
    queryKey: ['order-record-detail-items', recordId],
    queryFn: async () => {
      const response = await api.get<{
        contractor_record_id: string;
        title: string;
        created_by: string;
        created_by_name: string;
        total_items: number;
        master_data_type: string;
        word_zip_document_url: string;
        pdf_zip_document_url?: string | null;
        data: OrderRecordDetailListItem[];
      }>(`contractor-records/${recordId}`);
      // BFF 호환을 위해 필드명 변환
      return {
        worker_name: response.created_by_name,
        history_count: response.total_items,
        word_zip_document_url: response.word_zip_document_url,
        pdf_zip_document_url: response.pdf_zip_document_url || '',
        records: response.data,
      };
    },
    placeholderData: keepPreviousData,
  });

  const { mutate: exportRecords, isPending: isExportingRecords } = useMutation({
    mutationKey: [
      'export-order-record-files',
      recordId,
      selectedRows.map((row) => row.id).join(','),
    ],
    mutationFn: async ({
      recordId,
      itemIds,
      fileType,
    }: {
      recordId: string;
      itemIds: string[];
      fileType: 'docx' | 'pdf';
    }) =>
      await api.post<{ file_url: string; file_type: string }>(
        `contractor-records/${recordId}/file`,
        {
          item_ids: itemIds,
          file_type: fileType,
          view_mode: PRESIGNED_URL_VIEW_MODE.DOWNLOAD,
        }
      ),
    onSuccess: (data) => {
      window.open(data.file_url, '_blank');
    },
    onError: async (error) => {
      const errorMessage = await getErrorMessage(error);
      toast.error(errorMessage);
    },
  });

  const handleExportRecords = (type: 'docx' | 'pdf') => {
    exportRecords({
      recordId,
      itemIds: selectedRows.map((row) => row.id),
      fileType: type,
    });
  };

  return (
    <div className="w-full h-full flex flex-col p-7 space-y-2">
      <header className="flex flex-col gap-2 shrink-0">
        <Text
          tag="h1"
          typography="typo-title-m-bold"
        >
          기록서 조회
        </Text>
        <Text
          className="text-muted-foreground"
          typography="typo-text-xl"
        >
          생성된 기록서들을 조회할 수 있습니다.
        </Text>
      </header>

      <section className="flex-1 mt-6 gap-3 relative flex flex-col min-h-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5">
              <UserIcon className="size-4" />
              <Text typography="typo-text-xl">작업자:</Text>
              <Text typography="typo-text-xl-medium">{tableData?.worker_name}</Text>
            </div>
            <Separator
              className="bg-border !h-4"
              orientation="vertical"
            />
            <div className="flex items-center gap-1.5">
              <FileStack className="size-4" />
              <Text typography="typo-text-xl">생성:</Text>
              <Text typography="typo-text-xl-medium">{tableData?.history_count}</Text>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              disabled={selectedRows.length === 0}
              onClick={() => {
                handleExportRecords('docx');
              }}
              pending={isExportingRecords}
              size="sm"
              variant="outline"
            >
              Word 다운로드
            </Button>
            <Button
              disabled={selectedRows.length === 0}
              onClick={() => {
                handleExportRecords('pdf');
              }}
              pending={isExportingRecords}
              size="sm"
              variant="outline"
            >
              PDF 다운로드
            </Button>
          </div>
        </div>
        <DataTable<OrderRecordDetailListItem, unknown>
          columns={createOrderRecordColumns(recordId)}
          data={tableData?.records || []}
          enablePagination={false}
          isLoading={isLoading}
          onRowSelectionChange={setSelectedRows}
        />
      </section>

      <div className="flex items-center justify-center gap-4 py-4">
        <Button onClick={() => navigate(-1)}>목록</Button>
      </div>
    </div>
  );
}
